import React from "react";
import { useTranslation } from "react-i18next";
import { Radio, RadioGroup } from "@mui/material";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import dayjs from "dayjs";
import { useFormContext, useWatch } from "react-hook-form";
import { AlertCircle } from "lucide-react";
import BaseTooltip from "@/components/reusables/BaseTooltip";
import {
    AddProductFormSchemeTyping,
    DiscountOption,
} from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ProductSection/modals/addProductDraftShared";
import {
    isDiscountDateActive,
    isDiscountOptionEligible,
} from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ProductSection/modals/discountSelection";

interface DiscountCardProps {
    discounts: DiscountOption[];
    selectedDiscount?: DiscountOption;
    onDiscountSelect: (discount: DiscountOption) => void;
}

const DiscountCard = ({ discounts, selectedDiscount, onDiscountSelect }: DiscountCardProps) => {
    const { t } = useTranslation("", {
        keyPrefix: "sales.orders.general.orders.modals.addProduct.cards",
    });
    const { control } = useFormContext<AddProductFormSchemeTyping>();

    const quantity = useWatch({
        control,
        name: "quantity",
    });

    const priceDiscounts = discounts.filter(
        (discount) => discount.section === "pricesAndDiscounts"
    );
    const customerDiscounts = discounts.filter((discount) => discount.section === "customer");
    const groupDiscounts = discounts.filter((discount) => discount.section === "customerGroup");

    const today = dayjs();

    // Function to render discount cards for a given group
    const renderDiscountCards = (group: DiscountOption[]) => {
        return group.map((discount: DiscountOption) => {
            const isNotEnoughQuantity =
                !!discount.min_quantity && (!quantity || quantity < discount.min_quantity);
            const isNotDateValid = !isDiscountDateActive(discount, today);
            const isDisabled = !isDiscountOptionEligible(discount, quantity ?? 0, today);
            const isSelected = discount.id === selectedDiscount?.id;

            return (
                <div
                    key={discount.id}
                    className={`flex flex-row items-start max-w-sm p-4 border rounded-lg cursor-pointer ${
                        isDisabled
                            ? "bg-white opacity-50 cursor-not-allowed"
                            : isSelected
                            ? "bg-purple-100"
                            : "bg-white hover:outline outline-1 outline-offset-0 outline-purple-300"
                    }`}
                    onClick={() => !isDisabled && onDiscountSelect(discount)}
                >
                    <Radio
                        value={discount.id}
                        checked={isSelected}
                        disabled={isDisabled}
                        inputProps={{ "aria-label": getDiscountName(discount) }}
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                        onChange={() => onDiscountSelect(discount)}
                    />
                    <div className="flex-grow-1">
                        <div className="text-lg font-medium flex justify-between">
                            {getDiscountName(discount)}
                            <BaseTooltip
                                hidden={!isDisabled}
                                title={
                                    isNotDateValid
                                        ? (t("fields.validFrom.tooltip", {
                                              validFrom: dayjs(discount.valid_from).format(
                                                  "DD.MM.YYYY"
                                              ),
                                          }) as string)
                                        : (t("fields.minQuantity.tooltip", {
                                              minQuantity: discount.min_quantity,
                                          }) as string)
                                }
                            >
                                <AlertCircle className="h-6 w-6 text-accent" />
                            </BaseTooltip>
                        </div>
                        <div className="text-gray-600 font-semibold">
                            {t("fields.salePrice.label")}: {discount.basePrice}
                        </div>
                        {discount.min_quantity && (
                            <div className="text-gray-600 font-semibold">
                                {t("fields.minQuantity.label")}: {discount.min_quantity}
                            </div>
                        )}
                        <div className="text-gray-600 font-semibold">
                            {t("fields.discount.label")}: {discount.discount}%
                        </div>
                        <div className="text-gray-600">
                            {t("fields.calculatedPrice.label")}: {discount.calculated_price}
                        </div>
                        {(discount.valid_from || discount.valid_to) && (
                            <>
                                <div className="text-gray-600">
                                    {t("fields.validFrom.label")}:{" "}
                                    {discount.valid_from &&
                                        dayjs(discount.valid_from).format("DD.MM.YYYY")}
                                </div>
                                <div className="text-gray-600">
                                    {t("fields.validTo.label")}:{" "}
                                    {discount.valid_to
                                        ? dayjs(discount.valid_to).format("DD.MM.YYYY")
                                        : t("fields.validTo.noExpiration")}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            );
        });
    };

    return (
        <RadioGroup name="discountOptions" value={selectedDiscount?.id ?? ""}>
            <BaseInputsGrid cols={2}>
                {renderSection(t("titles.pricesAndDiscounts"), priceDiscounts)}
                {renderSection(t("titles.generalCustomerDiscount"), customerDiscounts)}
                {renderSection(t("titles.customerGroup"), groupDiscounts)}
            </BaseInputsGrid>
        </RadioGroup>
    );

    function renderSection(title: string, discounts: DiscountOption[]) {
        return (
            discounts.length > 0 && (
                <>
                    <div className={`col-span-2 text-xl text-accent`}>{title}</div>
                    {renderDiscountCards(discounts)}
                </>
            )
        );
    }

    function getDiscountName(discount: DiscountOption): string {
        switch (discount.source) {
            case "personalProduct":
                return t("candidateNames.personalProduct") as string;
            case "personalCategory":
                return t("candidateNames.personalCategory") as string;
            case "customer":
                return t("candidateNames.customer") as string;
            case "groupProduct":
                return t("candidateNames.groupProduct", { group: discount.name }) as string;
            case "groupCategory":
                return t("candidateNames.groupCategory", { group: discount.name }) as string;
        }
    }
};

export default DiscountCard;
